import React from "react";
import * as Yup from "yup";
import { Formik, Form } from "formik";

import { useAppDispatch, useAppSelector } from "@/redux/store";
import {fetchSendCode, fetchSendPhone} from "@/redux/profile/settings/changePass/asyncActions";

import styles from "./SmsCodeForm.module.scss";

import { SubmitValueType } from "./types";
import { AuthCodeInput as AuthCode } from "@/components/Inputs/AuthCodeInput";
import {useInterval} from "@/hooks/useInterval";
import { phoneMaskFormat } from "@/utils/form/phoneMask";
import { getSendCodeErrorMessage } from "@/utils/sendCodeError";

const SMS_COOLDOWN_KEY = 'sms_last_sent';
const SMS_COOLDOWN_MS = 60000;

function getSmsCooldownRemaining(): number {
  const lastSent = sessionStorage.getItem(SMS_COOLDOWN_KEY);
  if (!lastSent) return 0;
  const elapsed = Date.now() - Number(lastSent);
  return elapsed < SMS_COOLDOWN_MS ? Math.ceil((SMS_COOLDOWN_MS - elapsed) / 1000) : 0;
}

type SmsCodeFormProps = {
  handleSubmitForm?: () => void
}

export const SmsCodeForm: React.FC<SmsCodeFormProps> = (props) => {
  const { handleSubmitForm } = props;
  const { phone } = useAppSelector(state => state.changePass);
  const [errorCode, setErrorCode] = React.useState(false)
  const [resendError, setResendError] = React.useState("")
  const [timer, setTimer] = React.useState(() => {
    const remaining = getSmsCooldownRemaining();
    return remaining > 0 ? remaining : 60;
  })
  const dispatch = useAppDispatch();
  const initialValues = {
    code: [""],
  };

  const validationSchema = Yup.object({
    code: Yup.array().of(Yup.string().required("Required")),
  });

  const handleSubmit = async (values: SubmitValueType) => {
    const code = values.code.join("");

    const data = {
      phone,
      code: Number(code),
    };

    try {
      await dispatch(fetchSendCode(data)).unwrap();
      
      handleSubmitForm && handleSubmitForm()
    } catch (error) {
      setErrorCode(true)
    }
  };

  useInterval(() => {
    setTimer(timer - 1)
  }, timer > 0? 1000 : null)

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      validateOnChange={false}
      validateOnBlur={false}
    >
      {(formik) => {
        return (
          <Form className={styles.form}>
            <p className={styles.description}>
              Вам был выслан код подтверждения <br/> на телефон {' '} {phoneMaskFormat(phone)}
            </p>

            <div className={styles.formSection}>
              <div className={styles.inputsWrapper}>
                <AuthCode
                  containerClassName={styles.inputsWrapper}
                  length={4}
                  inputClassName={`${styles.input} ${errorCode ? styles.input_error : ''}`}
                  allowedCharacters='numeric'
                  onChange={(v)=>{
                    formik.setValues({code:[v]})

                    if (v.length === 4) {
                      formik.handleSubmit()
                    }
                  }}
                />
              </div>
              {
                errorCode ? (
                  <div className={`${styles.loadingMessage_error}`}>
                    Неверный код
                  </div>
                ) : null
              }
              {resendError ? (
                <div className={`${styles.loadingMessage_error}`}>{resendError}</div>
              ) : null}
            </div>

            <div className={styles.description}>
              {
                timer !== 0 
                  ? (
                    <span className={styles.timer}>
                      Повторить через {' '}
                      <span className={styles.timer_time}>
                        {timer} секунд
                      </span>
                    </span>
                  ) :(
                    <span
                      className={styles.resend}
                      onClick={async () => {
                        const result = await dispatch(fetchSendPhone({phone}));
                        // Код не ушёл — таймер не запускаем, чтобы можно было сразу повторить.
                        if (!fetchSendPhone.fulfilled.match(result)) {
                          setResendError(getSendCodeErrorMessage(result.payload));
                          return;
                        }
                        setResendError("");
                        sessionStorage.setItem(SMS_COOLDOWN_KEY, String(Date.now()));
                        setTimer(60)
                      }}
                    >
                      Отправить повторно
                    </span>
                  )
              }
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};
